/* eslint no-underscore-dangle: "off" */
/* eslint no-param-reassign: "off" */
<template>
  <div class="tree-container" ref="container">
    <svg ref="svg">
      <!-- Define arrow marker for directed links -->
      <defs>
        <marker
          :id="arrowMarkerId"
          viewBox="0 0 700 700"
          refX="700"
          refY="380"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <g transform="matrix(0,-1,1,0,28.57143,680.00001)">
            <!-- eslint-disable-next-line max-len -->
            <path d="M 680.00001,-27.858649 C 680.00001,-26.000219 330.00773,672.12987 329.42789,671.42805 C 327.4824,669.07328 -20.639627,-28.032809 -19.999107,-28.291219 C -19.580737,-28.459999 59.298143,5.3997002 155.28729,46.952552 L 329.81303,122.50321 L 504.05513,46.965887 C 599.8883,5.4203742 678.68037,-28.571419 679.14863,-28.571419 C 679.61689,-28.571419 680.00001,-28.250669 680.00001,-27.858649 z"
                  style="fill:#B331CF;fill-opacity:1;stroke:none"
            />
          </g>
        </marker>
      </defs>
      <!-- Two layers: trees are drawn first, custom links on top -->
      <g ref="treesContainer"></g>
      <g ref="customLinksLayer"></g>
    </svg>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */

import * as d3 from 'd3';
import domain from '@/assets/icons/ctc/domain.svg';
import subdomain from '@/assets/icons/ctc/subdomain.svg';
import ip from '@/assets/icons/ctc/ip.svg';
import custom from '@/assets/icons/ctc/custom.svg';
import email from '@/assets/icons/ctc/email.svg';
import phishing from '@/assets/icons/ctc/phishing.svg';
import threat from '@/assets/icons/ctc/threat.svg';
import actor from '@/assets/icons/ctc/actor.svg';
import { nodeTypes } from '@/constants/threatCanvas';
import { mapGetters } from 'vuex';

export default {
  name: 'CollapsableLinkedMultiTidyTree',
  props: {
    trees: { type: Array, required: true },
    customLinks: { type: Array, default: () => [] },
    properties: {
      type: Object,
      required: false,
      default: () => ({
        type: 'canvas',
        description: '',
      }),
    },
    newNodePosition: {
      type: Object,
      default: () => ({ x: 0, y: 0 }),
    },
    loadedCanvasData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      treeOffsets: [],
      nodeMap: {},
      roots: [],
      expandedStates: {},
      dimensions: {
        nodeWidth: 120,
        nodeHeight: 80,
        rectangleNodeWidth: 120,
        rectangleNodeHeight: 25,
        circleNodeRadius: 18,
        nodeSpacing: 15,
      },
      tempLink: null,
      creatingLinkSourceId: null,
      isLinkCreationActive: false,
    };
  },
  computed: {
    ...mapGetters('theme', ['isDarkMode']),
    arrowMarkerId() {
      return `arrow-${this._uid}`;
    },
  },
  mounted() {
    this.svgSel = d3.select(this.$refs.svg);
    this.treesContainer = d3.select(this.$refs.treesContainer);
    this.customLinksLayer = d3.select(this.$refs.customLinksLayer);
    this.iconCache = {};
    this.preloadThemedIcons().then(() => {
      this.drawTrees();
    });
    this.zoom = d3
      .zoom()
      .scaleExtent([0.1, 3])
      .on('zoom', event => {
        this.treesContainer.attr('transform', event.transform);
        this.customLinksLayer.attr('transform', event.transform);
      });
    this.svgSel.call(this.zoom).on('dblclick.zoom', null);
    this.svgSel.on('click', event => {
      if (event.target.tagName.toLowerCase() === 'svg') {
        d3.selectAll('g.node').classed('selected-node', false);
        d3.selectAll('path.tree-link').classed('selected-link', false);
        d3.selectAll('path.custom-link').classed('selected-link', false);
        this.$emit('canvas-selected', {
          ...(this.properties || { type: 'canvas', description: '' }),
        });
      }
    });
  },
  methods: {
    parseTranslate(transform) {
      const match = /translate\(([-\d.]+),\s*([-\d.]+)\)/.exec(transform);
      return match ? [parseFloat(match[1]), parseFloat(match[2])] : [0, 0];
    },
    positionChildNodes(node) {
      if (!node || !node.children || node.children.length === 0) return;

      const spacing = this.dimensions.nodeWidth + this.dimensions.nodeSpacing;
      const levelCorrection = 60;

      const calculateSubtreeWidth = childNode => {
        if (!childNode.children || childNode.children.length === 0) return spacing;
        let width = 0;
        childNode.children.forEach(child => {
          width += calculateSubtreeWidth(child);
        });
        return Math.max(width, spacing);
      };

      const childrenWidths = node.children.map(c => calculateSubtreeWidth(c));
      const totalWidth = childrenWidths.reduce((a, b) => a + b, 0);
      let leftMost = (node.x || 0) - totalWidth / 2;

      for (let i = 0; i < node.children.length; i += 1) {
        const child = node.children[i];
        const childWidth = childrenWidths[i];

        child.x = leftMost + childWidth / 2 - (node.data?.properties?.type === 'subdomain' ? 0 : levelCorrection);
        leftMost += childWidth;

        // recursively position deeper nodes
        this.positionChildNodes(child);
      }
    },
    adjustNodePositions(node) {
      if (!node || !node.children) return;
      if (node.data?.properties?.type === 'domain' && node.children) {
        if (node.children.length === 1) {
          node.children[0].x = (node.x || 0) + 60;
          if (node.children[0].children) this.positionChildNodes(node.children[0]);
        } else if (node.children.length === 2) {
          const leftChild = node.children[0];
          const rightChild = node.children[1];
          const leftChildrenCount = leftChild?.children ? leftChild.children.length : 0;
          const rightChildrenCount = rightChild?.children ? rightChild.children.length : 0;
          const maxChildrenWidth = Math.max(leftChildrenCount, rightChildrenCount) * (this.dimensions.nodeWidth + this.dimensions.nodeSpacing);
          const adjustedOffset = Math.max(60, maxChildrenWidth / 2);
          leftChild.x = (node.x || 0) + 60 - adjustedOffset;
          rightChild.x = (node.x || 0) + 60 + adjustedOffset;
          if (leftChild?.children) this.positionChildNodes(leftChild);
          if (rightChild?.children) this.positionChildNodes(rightChild);
        }
        return;
      }
      this.positionChildNodes(node);
    },
    drawTrees(collapse = true) {
      // Store the current expanded states and positions before redrawing
      const expandedStates = {};
      const nodePositions = {};
      this.roots.forEach(root => {
        if (root) {
          root.descendants().forEach(node => {
            if (node.data && node.data.id) {
              const nodeId = String(node.data.id);
              expandedStates[nodeId] = {
                expanded: node.children !== null,
                _children: node._children,
                treeIndex: this.roots.indexOf(root),
              };
              nodePositions[nodeId] = {
                x: node.x,
                y: node.y,
                treeIndex: this.roots.indexOf(root),
              };
            }
          });
        }
      });

      this.treesContainer.selectAll('*').remove();
      this.customLinksLayer.selectAll('*').remove();
      if (!this.treeOffsets || !Array.isArray(this.treeOffsets)) {
        this.treeOffsets = [];
      }
      if (!this.nodeMap) {
        this.nodeMap = {};
      }
      if (!this.roots || !Array.isArray(this.roots)) {
        this.roots = [];
      }
      if (!this.trees || !Array.isArray(this.trees)) {
        console.warn('Invalid trees prop:', this.trees);
        return;
      }
      const vm = this;
      vm.trees.forEach((treeData, index) => {
        if (!treeData) {
          console.warn(`Tree data at index ${index} is undefined or null`);
          return;
        }
        const root = d3.hierarchy(treeData, d => d && d.children);
        if (!root) {
          console.warn(`Failed to create hierarchy for tree at index ${index}`);
          return;
        }

        // Restore expanded states for existing nodes
        root.descendants().forEach(node => {
          if (node && node.data && node.data.id) {
            const nodeId = String(node.data.id);
            const state = expandedStates[nodeId];
            if (state) {
              // Restore expanded state
              if (!state.expanded && node.children) {
                node._children = node.children;
                node.children = null;
              } else if (state.expanded && state._children) {
                node.children = state._children;
                node._children = null;
              }
            } else if (node.depth > 0 && collapse) {
              // For new nodes, only collapse if they're not direct children of root
              if (node.children) {
                node._children = node.children;
                node.children = null;
              }
            }
          }
        });

        const treeLayout = d3.tree()
          .nodeSize([this.dimensions.nodeWidth, this.dimensions.nodeHeight])
          .separation((a, b) => (a.parent === b.parent ? 2 : 1));
        treeLayout(root);

        // If this is a new tree (index === trees.length - 1), position it at the mouse position
        if (index === vm.trees.length - 1 && vm.newNodePosition && vm.newNodePosition.x !== 0 && vm.newNodePosition.y !== 0) {
          const svgNode = vm.svgSel.node();
          const point = svgNode.createSVGPoint();
          point.x = vm.newNodePosition.x;
          point.y = vm.newNodePosition.y;
          const svgPoint = point.matrixTransform(svgNode.getScreenCTM().inverse());

          // Calculate the offset needed to position the root at the mouse position
          const rootX = root.x || 0;
          const rootY = root.y || 0;
          const offsetX = svgPoint.x - rootX;
          const offsetY = svgPoint.y - rootY;

          // Apply the offset to all nodes in the tree
          root.descendants().forEach(node => {
            node.x = (node.x || 0) + offsetX;
            node.y = (node.y || 0) + offsetY;
          });
        } else {
          // Restore positions for existing nodes
          root.descendants().forEach(node => {
            if (node && node.data && node.data.id) {
              const nodeId = String(node.data.id);
              const position = nodePositions[nodeId];
              if (position) {
                node.x = position.x;
                node.y = position.y;
              }
            }
          });
        }

        vm.adjustNodePositions(root);
        vm.roots[index] = root;
        root.descendants().forEach(node => {
          if (node && node.data && node.data.id) {
            vm.nodeMap[String(node.data.id)] = {
              x: node.x || 0,
              y: node.y || 0,
              data: node.data,
              treeIndex: index,
            };
            // Update expanded states
            vm.expandedStates[String(node.data.id)] = {
              expanded: node.children !== null,
              _children: node._children,
              treeIndex: index,
            };
          }
        });
        // Create a group for this tree.
        const group = vm.treesContainer
          .append('g')
          .attr('class', 'tree-group')
          .attr('transform', `translate(${vm.treeOffsets[index]?.x || 0},${vm.treeOffsets[index]?.y || 0})`)
          .datum(index);
        // Draw parent-child links.
        group.selectAll('path.tree-link')
          .data(root.descendants().slice(1))
          .enter()
          .append('path')
          .attr('class', 'link tree-link')
          .attr('d', d => {
            const totalChildren = d.parent.children.length;
            const localIndex = d.parent.children.indexOf(d);
            const rectangleTypes = ['domain', 'subdomain', 'ip', 'email'];

            let sourceX = d.parent.x;
            let sourceY = d.parent.y;
            let targetX = d.x;
            let targetY = d.y;

            if (rectangleTypes.includes(d.parent.data.properties?.type)) {
              sourceX = d.parent.x + 60;
              sourceY = d.parent.y + 12.5;
            }
            if (rectangleTypes.includes(d.data.properties?.type)) {
              targetX = d.x + 60;
              targetY = d.y + 12.5;
            }

            if (totalChildren === 1 || (totalChildren % 2 === 1 && localIndex === Math.floor(totalChildren / 2))) {
              return `M${sourceX},${sourceY} L${targetX},${targetY}`;
            }
            const midY = (sourceY + targetY) / 2;
            return `M${sourceX},${sourceY} C${sourceX},${midY} ${targetX},${midY} ${targetX},${targetY}`;
          });
        // Draw nodes.
        const nodes = group.selectAll('g.node')
          .data(root.descendants())
          .enter()
          .append('g')
          .attr('class', 'node')
          .attr('transform', d => `translate(${d.x},${d.y})`)
          .on('click', (event, d) => {
            if (event.defaultPrevented) return;
            event.stopPropagation();
            d3.selectAll('g.node').classed('selected-node', false);
            d3.selectAll('path.tree-link').classed('selected-link', false);
            d3.selectAll('path.custom-link').classed('selected-link', false);
            d3.select(event.currentTarget).classed('selected-node', true);
            vm.$emit('node-selected', d.data);
          })
          .on('dblclick', (event, d) => {
            event.stopPropagation();
            if (d.children) {
              d._children = d.children;
              d.children = null;
            } else if (d._children) {
              d.children = d._children;
              d._children = null;
            }
            vm.updateTree(vm.nodeMap[String(d.data.id)].treeIndex);
          });
        // Append shapes for nodes.
        nodes.each((d, i, nodeElements) => {
          const nodeType = (d.data.properties && d.data.properties.type) || 'default';
          if (['domain', 'subdomain', 'ip', 'email'].includes(nodeType)) {
            d3.select(nodeElements[i])
              .append('rect')
              .attr('x', 0)
              .attr('y', 0)
              .attr('width', 120)
              .attr('height', 25)
              .attr('fill', 'transparent');
          } else {
            d3.select(nodeElements[i])
              .append('circle')
              .attr('r', (nodeType === 'custom' ? 24 : 12))
              .attr('fill', 'transparent');
          }
        });
        // Draw node content.
        nodes.each((d, i, nodeElements) => {
          const selection = d3.select(nodeElements[i]);
          this.drawNodeContent(selection, d);
        });
        // Attach drag behavior.
        group.call(
          d3.drag()
            .on('start', event => {
              event.sourceEvent.stopPropagation();
              const transform = group.attr('transform') || 'translate(0,0)';
              const [startX, startY] = vm.parseTranslate(transform);
              group.node().__dragContext = { startX, startY, startMouse: [event.x, event.y] };
            })
            .on('drag', event => {
              const ctx = group.node().__dragContext;
              if (!ctx) return;
              const dx = event.x - ctx.startMouse[0];
              const dy = event.y - ctx.startMouse[1];
              const newX = ctx.startX + dx;
              const newY = ctx.startY + dy;
              group.attr('transform', `translate(${newX},${newY})`);
              const treeIndex = group.datum();
              vm.$set(vm.treeOffsets, treeIndex, { x: newX, y: newY });
              vm.drawCustomLinks();
            })
            // eslint-disable-next-line no-unused-vars
            .on('end', event => {
              const ctx = group.node().__dragContext;
              if (!ctx) return;
              const transform = group.attr('transform');
              const [finalX, finalY] = vm.parseTranslate(transform);
              const treeIndex = group.datum();
              vm.$set(vm.treeOffsets, treeIndex, { x: finalX, y: finalY });
              vm.drawCustomLinks();
              delete group.node().__dragContext;
            }),
        );
      });
      this.drawCustomLinks();
    },

    // Draw custom links
    drawCustomLinks() {
      const vm = this;
      const layer = this.customLinksLayer;
      layer.selectAll('path.custom-link').remove();
      layer.selectAll('text.link-label').remove(); // Remove any existing labels
      const validLinks = vm.customLinks.filter(link => {
        const srcNode = vm.nodeMap[String(link.sourceId)];
        const tgtNode = vm.nodeMap[String(link.targetId)];
        if (!srcNode || !tgtNode) return false;
        const srcTreeNode = vm.roots[srcNode.treeIndex].descendants()
          .find(n => String(n.data.id) === String(link.sourceId));
        const tgtTreeNode = vm.roots[tgtNode.treeIndex].descendants()
          .find(n => String(n.data.id) === String(link.targetId));
        return srcTreeNode && tgtTreeNode;
      });

      // Draw the links
      const links = layer.selectAll('path.custom-link')
        .data(validLinks, link => `${link.sourceId}-${link.targetId}`)
        .enter()
        .append('path')
        .attr('class', 'link custom-link')
        .attr('d', link => {
          const srcNode = vm.nodeMap[String(link.sourceId)];
          const tgtNode = vm.nodeMap[String(link.targetId)];
          let srcX; let srcY; let tgtX; let
            tgtY;
          const srcAbsX = srcNode.x + vm.treeOffsets[srcNode.treeIndex].x;
          const srcAbsY = srcNode.y + vm.treeOffsets[srcNode.treeIndex].y;
          const tgtAbsX = tgtNode.x + vm.treeOffsets[tgtNode.treeIndex].x;
          const tgtAbsY = tgtNode.y + vm.treeOffsets[tgtNode.treeIndex].y;
          if (['domain', 'subdomain', 'ip', 'email'].includes(srcNode.data.properties?.type)) {
            const rectWidth = 120;
            const rectHeight = 25;
            const isTargetAbove = tgtAbsY < srcAbsY;
            if (isTargetAbove) {
              srcX = srcAbsX + rectWidth / 2;
              srcY = srcAbsY;
            } else {
              srcX = srcAbsX + rectWidth / 2;
              srcY = srcAbsY + rectHeight;
            }
          } else {
            const r = (srcNode.data.properties?.type === 'custom' ? 18 : 18);
            const isTargetAbove = tgtAbsY < srcAbsY;
            if (isTargetAbove) {
              srcX = srcAbsX;
              srcY = srcAbsY - r;
            } else {
              srcX = srcAbsX;
              srcY = srcAbsY + r;
            }
          }
          if (['domain', 'subdomain', 'ip', 'email'].includes(tgtNode.data.properties?.type)) {
            const rectWidth = 120;
            const rectHeight = 25;
            if (tgtAbsX > srcAbsX + rectWidth) {
              tgtX = tgtAbsX;
              tgtY = tgtAbsY + rectHeight / 2;
            } else if (tgtAbsX < srcAbsX) {
              tgtX = tgtAbsX + rectWidth;
              tgtY = tgtAbsY + rectHeight / 2;
            } else {
              tgtX = tgtAbsX + rectWidth / 2;
              tgtY = srcAbsY > tgtAbsY ? tgtAbsY + rectHeight : tgtAbsY;
            }
          } else {
            const r = (tgtNode.data.properties?.type === 'custom' ? 18 : 18);
            const dx = srcAbsX - tgtAbsX;
            const dy = srcAbsY - tgtAbsY;
            const angle = Math.atan2(dy, dx);
            tgtX = tgtAbsX + Math.cos(angle) * r;
            tgtY = tgtAbsY + Math.sin(angle) * r;
          }
          const dx = tgtX - srcX;
          const dy = tgtY - srcY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const offset = Math.min(distance / 3, 60);
          const isTargetAbove = tgtY < srcY;
          const curveDirection = isTargetAbove ? -1 : 1;
          const c1x = srcX + dx * 0.25;
          const c1y = srcY + dy * 0.25 + curveDirection * offset;
          const c2x = srcX + dx * 0.75;
          const c2y = srcY + dy * 0.75 + curveDirection * offset;
          return `M${srcX},${srcY} C ${c1x},${c1y} ${c2x},${c2y} ${tgtX},${tgtY}`;
        })
        .attr('marker-end', `url(#${vm.arrowMarkerId})`)
        .attr('marker-start', link => (link.properties?.bidirectional ? `url(#${vm.arrowMarkerId})` : null))
        .on('click', event => {
          event.stopPropagation();
          d3.selectAll('g.node').classed('selected-node', false);
          d3.selectAll('path.tree-link').classed('selected-link', false);
          d3.selectAll('path.custom-link').classed('selected-link', false);
          d3.selectAll('text.link-label').classed('selected-label', false);
          d3.select(event.currentTarget).classed('selected-link', true);
          d3.select(event.currentTarget.nextSibling).classed('selected-label', true);
          const linkData = d3.select(event.currentTarget).datum();
          vm.$emit('link-selected', {
            type: 'link',
            sourceId: linkData.sourceId,
            targetId: linkData.targetId,
            properties: linkData.properties,
          });
        });

      // Add labels for links that have them
      links.each((link, i, nodes) => {
        if (link.properties?.label) {
          const path = d3.select(nodes[i]);
          const pathLength = path.node().getTotalLength();
          const pathPoint = path.node().getPointAtLength(pathLength / 2);
          // Create a group for the label and its background
          const labelGroup = layer.append('g')
            .attr('class', 'link-label-group');
          // Add background rectangle
          const text = labelGroup.append('text')
            .attr('class', 'link-label')
            .attr('x', pathPoint.x)
            .attr('y', pathPoint.y)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('font-size', '10px')
            .attr('fill', '#666')
            .text(link.properties.label);
          // Get text dimensions
          const bbox = text.node().getBBox();
          const padding = 2;
          // Add background rectangle
          labelGroup.insert('rect', 'text')
            .attr('x', bbox.x - padding)
            .attr('y', bbox.y - padding)
            .attr('width', bbox.width + (padding * 2))
            .attr('height', bbox.height + (padding * 2))
            .attr('fill', 'white')
            .attr('rx', 2)
            .attr('ry', 2);
        }
      });
    },

    updateCustomLinkPositions(treeIndex) {
      this.customLinksLayer
        .selectAll('path.custom-link')
        .filter(link => this.nodeMap[String(link.sourceId)] &&
          this.nodeMap[String(link.targetId)] &&
          (this.nodeMap[String(link.sourceId)].treeIndex === treeIndex ||
            this.nodeMap[String(link.targetId)].treeIndex === treeIndex))
        .attr('d', link => {
          const srcNode = this.nodeMap[String(link.sourceId)];
          const tgtNode = this.nodeMap[String(link.targetId)];
          let srcX; let srcY; let tgtX; let
            tgtY;
          const srcAbsX = srcNode.x + this.treeOffsets[srcNode.treeIndex].x;
          const srcAbsY = srcNode.y + this.treeOffsets[srcNode.treeIndex].y;
          const tgtAbsX = tgtNode.x + this.treeOffsets[tgtNode.treeIndex].x;
          const tgtAbsY = tgtNode.y + this.treeOffsets[tgtNode.treeIndex].y;
          if (['domain', 'subdomain', 'ip', 'email'].includes(srcNode.data.properties?.type)) {
            const rectWidth = 120;
            const rectHeight = 25;
            const isTargetAbove = tgtAbsY < srcAbsY;
            if (isTargetAbove) {
              srcX = srcAbsX + rectWidth / 2;
              srcY = srcAbsY;
            } else {
              srcX = srcAbsX + rectWidth / 2;
              srcY = srcAbsY + rectHeight;
            }
          } else {
            const r = (srcNode.data.properties?.type === 'custom' ? 18 : 18);
            const isTargetAbove = tgtAbsY < srcAbsY;
            if (isTargetAbove) {
              srcX = srcAbsX;
              srcY = srcAbsY - r;
            } else {
              srcX = srcAbsX;
              srcY = srcAbsY + r;
            }
          }
          if (['domain', 'subdomain', 'ip', 'email'].includes(tgtNode.data.properties?.type)) {
            const rectWidth = 120;
            const rectHeight = 25;
            if (tgtAbsX > srcAbsX + rectWidth) {
              tgtX = tgtAbsX;
              tgtY = tgtAbsY + rectHeight / 2;
            } else if (tgtAbsX < srcAbsX) {
              tgtX = tgtAbsX + rectWidth;
              tgtY = tgtAbsY + rectHeight / 2;
            } else {
              tgtX = tgtAbsX + rectWidth / 2;
              tgtY = srcAbsY > tgtAbsY ? tgtAbsY + rectHeight : tgtAbsY;
            }
          } else {
            const r = (tgtNode.data.properties?.type === 'custom' ? 18 : 18);
            const dx = srcAbsX - tgtAbsX;
            const dy = srcAbsY - tgtAbsY;
            const angle = Math.atan2(dy, dx);
            tgtX = tgtAbsX + Math.cos(angle) * r;
            tgtY = tgtAbsY + Math.sin(angle) * r;
          }
          const dx = tgtX - srcX;
          const dy = tgtY - srcY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const offset = Math.min(distance / 3, 60);
          const isTargetAbove = tgtY < srcY;
          const curveDirection = isTargetAbove ? -1 : 1;
          const c1x = srcX + dx * 0.25;
          const c1y = srcY + dy * 0.25 + curveDirection * offset;
          const c2x = srcX + dx * 0.75;
          const c2y = srcY + dy * 0.75 + curveDirection * offset;
          return `M${srcX},${srcY} C ${c1x},${c1y} ${c2x},${c2y} ${tgtX},${tgtY}`;
        });
    },

    updateTree(treeIndex) {
      const vm = this;
      const root = vm.roots[treeIndex];
      if (!root) return;

      // Store current positions before updating
      const currentPositions = {};
      root.descendants().forEach(node => {
        if (node.data && node.data.id) {
          currentPositions[String(node.data.id)] = {
            x: node.x,
            y: node.y,
          };
        }
      });

      const treeLayout = d3.tree()
        .nodeSize([this.dimensions.nodeWidth, this.dimensions.nodeHeight])
        .separation((a, b) => (a.parent === b.parent ? 2 : 1));
      treeLayout(root);

      // Restore positions after layout, but only for existing nodes
      root.descendants().forEach(node => {
        if (node.data && node.data.id) {
          const storedPosition = currentPositions[String(node.data.id)];
          if (storedPosition && storedPosition.x !== undefined && storedPosition.y !== undefined) {
            node.x = storedPosition.x;
            node.y = storedPosition.y;
          }
        }
      });

      // Ensure root node is visible
      if (root.x === undefined || root.y === undefined) {
        root.x = 0;
        root.y = 0;
      }

      // Adjust node positions to ensure top-to-bottom layout
      const verticalSpacing = this.dimensions.nodeHeight;
      let maxDepth = 0;
      root.descendants().forEach(node => {
        if (node.depth > maxDepth) maxDepth = node.depth;
      });

      // Calculate the base Y position for the root
      const baseY = root.y;
      // Position nodes from top to bottom
      root.descendants().forEach(node => {
        if (node.depth > 0) {
          // Calculate Y position from top to bottom
          node.y = baseY + (node.depth * verticalSpacing);
        }
      });

      vm.adjustNodePositions(root);
      Object.keys(vm.nodeMap).forEach(id => {
        if (vm.nodeMap[id].treeIndex === treeIndex) delete vm.nodeMap[id];
      });
      root.descendants().forEach(node => {
        if (node.data && node.data.id) {
          vm.nodeMap[String(node.data.id)] = {
            x: node.x || 0,
            y: node.y || 0,
            data: node.data,
            treeIndex,
          };
          // Update expanded states
          vm.expandedStates[String(node.data.id)] = {
            expanded: node.children !== null,
            _children: node._children,
            treeIndex,
          };
        }
      });
      const group = vm.treesContainer.selectAll('g.tree-group').filter(d => d === treeIndex);
      group.selectAll('path.tree-link').remove();
      group.selectAll('g.node').remove();
      group.selectAll('path.tree-link')
        .data(root.descendants().slice(1))
        .enter()
        .append('path')
        .attr('class', 'link tree-link')
        .attr('d', d => {
          const totalChildren = d.parent.children.length;
          const localIndex = d.parent.children.indexOf(d);
          const rectangleTypes = ['domain', 'subdomain', 'ip', 'email'];

          let sourceX = d.parent.x;
          let sourceY = d.parent.y;
          let targetX = d.x;
          let targetY = d.y;

          if (rectangleTypes.includes(d.parent.data.properties?.type)) {
            sourceX = d.parent.x + 60;
            sourceY = d.parent.y + 12.5;
          }
          if (rectangleTypes.includes(d.data.properties?.type)) {
            targetX = d.x + 60;
            targetY = d.y + 12.5;
          }

          if (totalChildren === 1 || (totalChildren % 2 === 1 && localIndex === Math.floor(totalChildren / 2))) {
            return `M${sourceX},${sourceY} L${targetX},${targetY}`;
          }
          const midY = (sourceY + targetY) / 2;
          return `M${sourceX},${sourceY} C${sourceX},${midY} ${targetX},${midY} ${targetX},${targetY}`;
        })
        .on('click', (event, d) => {
          event.stopPropagation();
          d3.selectAll('g.node').classed('selected-node', false);
          d3.selectAll('path.tree-link').classed('selected-link', false);
          d3.selectAll('path.custom-link').classed('selected-link', false);
          d3.select(event.currentTarget).classed('selected-link', true);
          vm.$emit('link-selected', { type: 'tree', source: d.parent.data, target: d.data });
        });
      const nodes = group.selectAll('g.node')
        .data(root.descendants())
        .enter()
        .append('g')
        .attr('class', 'node')
        .attr('transform', d => `translate(${d.x || 0},${d.y || 0})`)
        .on('click', (event, d) => {
          if (event.defaultPrevented) return;
          event.stopPropagation();
          d3.selectAll('g.node').classed('selected-node', false);
          d3.selectAll('path.tree-link').classed('selected-link', false);
          d3.selectAll('path.custom-link').classed('selected-link', false);
          d3.select(event.currentTarget).classed('selected-node', true);
          vm.$emit('node-selected', d.data);
        })
        .on('dblclick', (event, d) => {
          event.stopPropagation();
          if (d.children) {
            d._children = d.children;
            d.children = null;
          } else if (d._children) {
            d.children = d._children;
            d._children = null;
          }
          vm.updateTree(treeIndex);
          vm.drawCustomLinks();
        });
      nodes.each((d, i, nodeElements) => {
        const nodeType = (d.data.properties && d.data.properties.type) || 'default';
        if (['domain', 'subdomain', 'ip', 'email'].includes(nodeType)) {
          d3.select(nodeElements[i])
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', 100)
            .attr('height', 25)
            .attr('fill', 'transparent');
        } else {
          d3.select(nodeElements[i])
            .append('circle')
            .attr('r', (nodeType === 'custom' ? 18 : 18))
            .attr('fill', 'transparent');
        }
      });
      nodes.each((d, i, nodeElements) => {
        const selection = d3.select(nodeElements[i]);
        this.drawNodeContent(selection, d);
      });
      vm.drawCustomLinks();
    },

    zoomIn() {
      this.svgSel.transition().duration(250).call(this.zoom.scaleBy, 1.2);
    },
    zoomOut() {
      this.svgSel.transition().duration(250).call(this.zoom.scaleBy, 0.8);
    },
    resetView() {
      const svgNode = this.svgSel.node();
      const width = svgNode.clientWidth;
      const height = svgNode.clientHeight;
      const bbox = this.treesContainer.node().getBBox();
      const centerX = bbox.x + bbox.width / 2;
      const centerY = bbox.y + bbox.height / 2;
      const translateX = width / 2 - centerX;
      const translateY = height / 2 - centerY;
      const transform = d3.zoomIdentity.translate(translateX, translateY);
      this.svgSel.transition().duration(250).call(this.zoom.transform, transform);
    },
    autoAlignTrees() {
      let x = this.dimensions.nodeWidth;
      const marginBetween = this.dimensions.nodeWidth + this.dimensions.nodeSpacing;
      this.roots.forEach((root, index) => {
        if (!root) return;
        let minX = Infinity;
        let maxX = -Infinity;
        root.descendants().forEach(node => {
          if (node.x < minX) { minX = node.x; }
          if (node.x > maxX) { maxX = node.x; }
        });
        if (!this.treeOffsets[index]) {
          this.treeOffsets[index] = {x: 0, y: 0};
        }
        // Calculate the new offset based on the tree's width and position
        const treeWidth = maxX - minX;
        this.treeOffsets[index].x = x - minX;
        this.treeOffsets[index].y = 20;
        x += treeWidth + marginBetween;
        // Apply the transform to the tree group
        this.treesContainer
          .selectAll('g.tree-group')
          .filter(d => d === index)
          .attr('transform', `translate(${this.treeOffsets[index].x},${this.treeOffsets[index].y})`);
      });
      this.drawCustomLinks();
    },
    updateCanvasDescription(data) {
      this.properties.description = data;
    },
    drawNodeContent(selection, d) {
      const nodeType = d.data.properties && d.data.properties.type ? d.data.properties.type : 'default';
      const truncateText = (text, maxChars = 5) => {
        if (!text) return '';
        return text.length > maxChars ? `${text.substring(0, maxChars)}...` : text;
      };
      if (nodeType === 'domain' || nodeType === 'subdomain' || nodeType === 'ip' || nodeType === 'email') {
        const rectWidth = this.dimensions.rectangleNodeWidth;
        const rectHeight = this.dimensions.rectangleNodeHeight;
        const iconSize = rectHeight - 6;
        selection.append('rect')
          .attr('class', 'node-shape')
          .attr('width', rectWidth)
          .attr('height', rectHeight)
          .attr('rx', 6)
          .attr('ry', 6)
          .attr('stroke-width', '2px');
        selection.append('image')
          .attr('href', this.loadIcon(nodeType))
          .attr('x', 3)
          .attr('y', 3)
          .attr('width', iconSize)
          .attr('height', iconSize);
        selection.append('text')
          .attr('x', iconSize + 7)
          .attr('y', rectHeight / 2)
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '10px')
          .text(truncateText(d.data.properties.name, 15));
      } else if (nodeType === 'subdomains_wrapper' || nodeType === 'email_wrapper') {
        selection.append('circle')
          .attr('class', 'node-shape')
          .attr('r', this.dimensions.circleNodeRadius)
          .attr('stroke-width', '2px');
        selection.append('image')
          .attr('href', this.loadIcon(nodeType))
          .attr('x', -15)
          .attr('y', -15)
          .attr('width', 30)
          .attr('height', 30);
        let childrenCount = 0;
        if (d.children) {
          childrenCount = d.children.length;
        } else if (d._children) {
          childrenCount = d._children.length;
        }
        if (childrenCount > 0) {
          const badgeText = childrenCount.toString();
          const badgeWidth = badgeText.length * 6 + 10;
          const badgeHeight = 15;
          const badgeX = 17 - badgeWidth / 2;
          const badgeY = -19 - badgeHeight / 2;
          selection.append('rect')
            .attr('class', 'node-badge')
            .attr('x', badgeX)
            .attr('y', badgeY)
            .attr('width', badgeWidth)
            .attr('height', badgeHeight)
            .attr('rx', 2)
            .attr('ry', 2)
            .attr('stroke-width', '1px');
          selection.append('text')
            .attr('class', 'node-badge-text')
            .attr('x', badgeX + badgeWidth / 2)
            .attr('y', badgeY + badgeHeight / 2)
            .attr('font-size', '8px')
            .attr('font-family', '"SF-Pro-Display-Medium", sans-serif')
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .text(badgeText);
        }
      } else if (nodeType === 'custom') {
        selection.append('circle')
          .attr('class', 'node-shape')
          .attr('r', this.dimensions.circleNodeRadius)
          .attr('stroke-width', '2px');
        selection.append('image')
          .attr('href', this.loadIcon(d.data.properties.icon))
          .attr('x', -15)
          .attr('y', -15)
          .attr('width', 30)
          .attr('height', 30);
        selection.append('text')
          .attr('x', 0)
          .attr('y', 24 + 7 + 10 / 2)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .text(d.data.properties.name);
      } else {
        selection.append('circle')
          .attr('r', 12)
          .attr('fill', 'transparent');
        selection.append('image')
          .attr('href', d.data?.icon)
          .attr('x', -12)
          .attr('y', -12)
          .attr('width', 24)
          .attr('height', 24);
      }
    },

    getIconColor() {
      return getComputedStyle(document.documentElement).getPropertyValue('--brand-secondary').trim();
    },
    preloadThemedIcons() {
      const iconColor = this.getIconColor();
      const iconUrls = {
        domain, subdomain, ip, email, custom, phishing, threat, actor,
      };
      const promises = Object.entries(iconUrls).map(([key, url]) => fetch(url)
        .then(res => res.text())
        .then(svgText => {
          const themed = svgText.replace(/currentColor/g, iconColor);
          this.iconCache[key] = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(themed)}`;
        })
        .catch(() => {
          this.iconCache[key] = url;
        }));
      return Promise.all(promises);
    },
    loadIcon(type) {
      if (type === nodeTypes.domain) {
        return this.iconCache.domain || domain;
      }
      if (type === nodeTypes.subdomain || type === 'subdomains_wrapper') {
        return this.iconCache.subdomain || subdomain;
      }
      if (type === nodeTypes.ip) {
        return this.iconCache.ip || ip;
      }
      if (type === nodeTypes.email || type === 'email_wrapper') {
        return this.iconCache.email || email;
      }
      if (type === 'phishing') {
        return this.iconCache.phishing || phishing;
      }
      if (type === 'threat') {
        return this.iconCache.threat || threat;
      }
      if (type === 'actor') {
        return this.iconCache.actor || actor;
      }
      return this.iconCache.custom || custom;
    },
    startLinkCreation(sourceId) {
      // Ensure complete cleanup of any existing state
      this.cleanupLinkCreation();
      this.creatingLinkSourceId = sourceId;
      this.isLinkCreationActive = true;
      this.tempLink = this.customLinksLayer
        .append('path')
        .attr('class', 'link custom-link')
        .attr('marker-end', `url(#${this.arrowMarkerId})`)
        .attr('marker-start', `url(#${this.arrowMarkerId})`);

      // Add mouse move listener to the SVG
      this.svgSel.on('mousemove.link-creation', this.updateTempLink);
    },
    cleanupLinkCreation() {
      // Remove all event listeners
      this.svgSel.on('mousemove.link-creation', null);
      // Clean up temporary elements
      if (this.tempLink) {
        this.tempLink.remove();
        this.tempLink = null;
      }
      // Reset state
      this.creatingLinkSourceId = null;
      this.isLinkCreationActive = false;
    },
    stopLinkCreation() {
      this.cleanupLinkCreation();
    },
    updateTempLink(event) {
      if (!this.isLinkCreationActive || !this.creatingLinkSourceId || !this.tempLink) {
        this.cleanupLinkCreation();
        return;
      }

      const sourceNode = this.nodeMap[String(this.creatingLinkSourceId)];
      if (!sourceNode) {
        this.cleanupLinkCreation();
        return;
      }

      // Get the current transform and mouse position
      const svgNode = this.svgSel.node();
      const point = svgNode.createSVGPoint();
      point.x = event.clientX;
      point.y = event.clientY;
      const svgPoint = point.matrixTransform(svgNode.getScreenCTM().inverse());

      const sourceX = sourceNode.x + this.treeOffsets[sourceNode.treeIndex].x;
      const sourceY = sourceNode.y + this.treeOffsets[sourceNode.treeIndex].y;

      let startX;
      let startY;
      if (['domain', 'subdomain', 'ip', 'email'].includes(sourceNode.data.properties?.type)) {
        const rectWidth = 120;
        const rectHeight = 25;
        startX = sourceX + rectWidth / 2;
        startY = sourceY + rectHeight;
      } else {
        const r = (sourceNode.data.properties?.type === 'custom' ? 24 : 12);
        startX = sourceX;
        startY = sourceY + r;
      }

      const dx = svgPoint.x - startX;
      const dy = svgPoint.y - startY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const offset = Math.min(distance / 3, 60);
      const curveDirection = 1;
      const c1x = startX + dx * 0.25;
      const c1y = startY + dy * 0.25 + curveDirection * offset;
      const c2x = startX + dx * 0.75;
      const c2y = startY + dy * 0.75 + curveDirection * offset;

      this.tempLink.attr('d', `M${startX},${startY} C ${c1x},${c1y} ${c2x},${c2y} ${svgPoint.x},${svgPoint.y}`);
    },
    loadCanvasData(canvasData) {
      // Update component state with loaded data
      this.nodeMap = canvasData.nodePositions;
      this.treeOffsets = canvasData.treeOffsets;
      // Restore tree states before updating parent component
      const restoredTrees = canvasData.trees.map(treeData => {
        if (!treeData) return null;

        // Create d3 hierarchy from the tree data
        const root = d3.hierarchy(treeData, d => d.children);
        // Restore positions for each node in the hierarchy
        root.descendants().forEach(node => {
          if (node.data && node.data.id) {
            const position = this.nodeMap[node.data.id];
            if (position) {
              node.x = position.x;
              node.y = position.y;
            } else {
              node.parent._children = node.parent.children;
              node.parent.children = null;
            }
          }
        });

        return root;
      });

      // Store the restored trees
      this.roots = restoredTrees;

      // Redraw the canvas
      this.$nextTick(() => {
        this.drawTrees(false);
      });
    },
  },
  watch: {
    isDarkMode() {
      this.preloadThemedIcons().then(() => {
        this.drawTrees(false);
      });
    },
    trees: {
      handler() {
        this.drawTrees();
      },
      deep: true,
    },
    customLinks: {
      handler() {
        this.drawCustomLinks();
      },
      deep: true,
    },
    loadedCanvasData: {
      handler(newData) {
        if (newData) {
          this.loadCanvasData(newData);
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.tree-container {
  width: 100%;
  height: 100%;

  svg {
    width: 100%;
    height: 100%;

    ::v-deep {
      .tree-group {
        .link {
          fill: none;
          stroke: var(--text-tertiary);
          stroke-width: 2px;
          cursor: pointer;
        }

        .tree-link {
          stroke: var(--text-tertiary);
        }

        .node {
          cursor: pointer;

          // Theme-aware node shapes
          .node-shape {
            fill: var(--bg-primary);
            stroke: var(--brand-primary);
          }

          // Badge: brand fill, matching border to blend with background
          .node-badge {
            fill: var(--brand-primary);
            stroke: var(--bg-primary);
            filter: none;
          }

          .node-badge-text {
            fill: #ffffff;
          }

          text:not(.node-badge-text) {
            fill: var(--text-primary);
          }
        }

        .selected-link {
          stroke: var(--brand-primary);
          stroke-width: 2px;
        }

        .selected-node .node-shape {
          filter: drop-shadow(0px 0px 7px var(--brand-primary));
        }
      }

      .link {
        fill: none;
        stroke: var(--text-tertiary);
        stroke-width: 2px;
        cursor: pointer;
      }

      .tree-link {
        stroke: var(--text-tertiary);
      }

      .custom-link {
        stroke: var(--text-tertiary);
        stroke-dasharray: 4 3;
        stroke-width: 1px;
      }

      .custom-link.selected-link {
        stroke: var(--brand-primary);
        stroke-width: 2px;
        stroke-dasharray: none;
      }

      .link-label {
        pointer-events: none;
        user-select: none;
        fill: var(--text-secondary);
      }

      .link-label-group rect {
        fill: var(--bg-primary);
      }

      .link-label.selected-label {
        fill: var(--brand-primary);
        font-weight: bold;
      }
    }
  }
}
</style>
